import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="photo-review"
export default class extends Controller {
  static targets = [
    "activateBtn",
    "deactivateBtn",
    "prevBtn",
    "nextBtn",
    "currentPosition",
    "lastReviewedText",
  ];
  static values = {
    total: Number,
    current: Number,
    photoId: String,
  };

  connect() {
    console.log("Photo review controller connected");
    this.isUpdating = false;
    this.setupKeyboardListeners();
    this.setupTouchListeners();
    this.updateButtonStates();
    this.initializeButtonStates();
  }

  disconnect() {
    this.removeKeyboardListeners();
    this.removeTouchListeners();
  }

  // Navigation methods
  navigateToIndex(index) {
    if (index < 0 || index >= this.totalValue) return;

    const currentUrl = new URL(window.location);
    currentUrl.searchParams.set("index", index);
    window.location.href = currentUrl.toString();
  }

  previous() {
    if (this.currentValue > 0) {
      this.navigateToIndex(this.currentValue - 1);
    }
  }

  next() {
    if (this.currentValue < this.totalValue - 1) {
      this.navigateToIndex(this.currentValue + 1);
    }
  }

  // Status toggle methods
  async activate() {
    await this.togglePhotoStatus(true);
  }

  async deactivate() {
    await this.togglePhotoStatus(false);
  }

  async togglePhotoStatus(activate) {
    if (this.isUpdating || !this.photoIdValue) return;

    this.isUpdating = true;

    // Update button states immediately for better UX
    this.activateBtnTargets.forEach((btn) => (btn.disabled = true));
    this.deactivateBtnTargets.forEach((btn) => (btn.disabled = true));

    try {
      const response = await fetch(
        `/photos/${this.photoIdValue}/toggle_status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": this.getCSRFToken(),
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        this.updateButtonAppearance(data.new_status);

        // Update last reviewed timestamp if target exists
        if (this.hasLastReviewedTextTarget && data.last_reviewed_at) {
          this.lastReviewedTextTarget.textContent = data.last_reviewed_at;

          // Update the review status indicator
          const reviewStatusDiv =
            this.lastReviewedTextTarget.closest(".bg-gray-800");
          if (reviewStatusDiv) {
            const statusElement = reviewStatusDiv.querySelector(
              ".text-purple-400, .text-yellow-400"
            );
            if (
              statusElement &&
              statusElement.classList.contains("text-yellow-400")
            ) {
              statusElement.innerHTML = `
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span class="text-sm">Last reviewed</span>
              `;
              statusElement.className = statusElement.className.replace(
                "text-yellow-400",
                "text-purple-400"
              );
            }
          }
        }

        this.showToast(data.message, "success");
      } else {
        this.showToast("Error: " + data.message, "error");
      }
    } catch (error) {
      console.error("Error updating photo status:", error);
      this.showToast("Error updating photo status. Please try again.", "error");
    } finally {
      this.isUpdating = false;
      this.activateBtnTargets.forEach((btn) => (btn.disabled = false));
      this.deactivateBtnTargets.forEach((btn) => (btn.disabled = false));
    }
  }

  // Helper methods
  initializeButtonStates() {
    // This method ensures button states match the current photo's actual status
    // Check if we can determine the current status from the page data
    const container = document.querySelector(
      '[data-controller="photo-review"]'
    );
    if (!container) return;

    // Get the current photo status from the DOM by checking existing button states
    const firstActivateBtn = this.activateBtnTargets[0];
    if (!firstActivateBtn) return;

    // Determine current status based on button classes set by ERB
    const isCurrentlyActive =
      !firstActivateBtn.classList.contains("btn-outline");
    const currentStatus = isCurrentlyActive ? "active" : "hidden";

    // Log for debugging
    console.log(
      `Photo ID: ${this.photoIdValue}, Status: ${currentStatus}, IsActive: ${isCurrentlyActive}`
    );

    // Ensure all buttons match this state
    this.updateButtonAppearance(currentStatus);
  }

  updateButtonAppearance(status) {
    console.log(`Updating button appearance to: ${status}`);

    // Update all activate/deactivate buttons (both mobile and desktop)
    this.activateBtnTargets.forEach((btn) => {
      if (status === "active") {
        btn.classList.remove("btn-outline");
      } else {
        btn.classList.add("btn-outline");
      }
    });

    this.deactivateBtnTargets.forEach((btn) => {
      if (status === "active") {
        btn.classList.add("btn-outline");
      } else {
        btn.classList.remove("btn-outline");
      }
    });

    // Add visual debug indicator (remove this later)
    this.addDebugIndicator(status);
  }

  addDebugIndicator(status) {
    // Add a temporary debug indicator to help track the issue
    let debugDiv = document.querySelector("#debug-status");
    if (!debugDiv) {
      debugDiv = document.createElement("div");
      debugDiv.id = "debug-status";
      debugDiv.className =
        "fixed top-20 right-4 bg-red-600 text-white px-2 py-1 rounded text-xs z-50";
      document.body.appendChild(debugDiv);
    }
    debugDiv.textContent = `Status: ${status} | Photo: ${this.photoIdValue}`;
  }

  updateButtonStates() {
    // Update navigation button states (both mobile and desktop)
    this.prevBtnTargets.forEach((btn) => {
      btn.disabled = this.currentValue === 0;
    });

    this.nextBtnTargets.forEach((btn) => {
      btn.disabled = this.currentValue >= this.totalValue - 1;
    });
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]');
    return token ? token.content : "";
  }

  showToast(message, type) {
    const toast = document.createElement("div");
    toast.className = "toast toast-end z-50";
    const alertClass = type === "success" ? "alert-success" : "alert-error";
    toast.innerHTML = `
      <div class="alert ${alertClass}">
        <span>${message}</span>
      </div>
    `;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 3000);
  }

  goBack() {
    const backLink = document.querySelector('a[href*="/photos"]');
    if (backLink) {
      window.location.href = backLink.href;
    }
  }

  // Keyboard event handling
  setupKeyboardListeners() {
    this.keydownHandler = this.handleKeydown.bind(this);
    document.addEventListener("keydown", this.keydownHandler);
  }

  removeKeyboardListeners() {
    if (this.keydownHandler) {
      document.removeEventListener("keydown", this.keydownHandler);
    }
  }

  handleKeydown(event) {
    // Ignore if user is typing in an input field
    if (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA")
      return;

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        this.previous();
        break;

      case "ArrowRight":
        event.preventDefault();
        this.next();
        break;

      case "a":
      case "A":
        event.preventDefault();
        this.activate();
        break;

      case "d":
      case "D":
        event.preventDefault();
        this.deactivate();
        break;

      case "Escape":
        event.preventDefault();
        this.goBack();
        break;
    }
  }

  // Touch event handling for mobile
  setupTouchListeners() {
    this.touchStartX = null;
    this.touchStartHandler = this.handleTouchStart.bind(this);
    this.touchEndHandler = this.handleTouchEnd.bind(this);

    document.addEventListener("touchstart", this.touchStartHandler);
    document.addEventListener("touchend", this.touchEndHandler);
  }

  removeTouchListeners() {
    if (this.touchStartHandler) {
      document.removeEventListener("touchstart", this.touchStartHandler);
    }
    if (this.touchEndHandler) {
      document.removeEventListener("touchend", this.touchEndHandler);
    }
  }

  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
  }

  handleTouchEnd(event) {
    if (this.touchStartX === null) return;

    const touchEndX = event.changedTouches[0].clientX;
    const diff = this.touchStartX - touchEndX;

    // Minimum swipe distance
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        // Swipe left - next photo
        this.next();
      } else {
        // Swipe right - previous photo
        this.previous();
      }
    }

    this.touchStartX = null;
  }

  // Value change callbacks
  currentValueChanged() {
    this.updateButtonStates();
    if (this.hasCurrentPositionTarget) {
      this.currentPositionTarget.textContent = this.currentValue + 1;
    }
  }

  photoIdValueChanged() {
    // When photo ID changes (navigation), re-initialize button states
    console.log(`Photo changed to ID: ${this.photoIdValue}`);
    // Add a small delay to ensure DOM has updated with new photo data
    setTimeout(() => {
      this.initializeButtonStates();
    }, 50);
  }

  totalValueChanged() {
    this.updateButtonStates();
  }
}
